// Favicon Manager for dynamic favicon updates based on domain
export class FaviconManager {
  constructor() {
    this.currentFavicon = null;
    this.currentAppleTouchIcon = null;
    this.currentDomainId = null;
  }

  // Check if we're in a pre-rendering environment (react-snap)
  isPreRendering() {
    if (typeof window === 'undefined') return true;
    
    // During pre-rendering with react-snap:
    // - We're on localhost
    // - But REACT_APP_DOMAIN_ID is set (build-time)
    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const hasBuildTimeDomainId = process.env.REACT_APP_DOMAIN_ID;
    
    // If we're on localhost but have a build-time domain ID, we're pre-rendering
    // Don't manipulate DOM - the build already injected the correct favicon
    return isLocalhost && hasBuildTimeDomainId;
  }

  // Update favicon based on domain configuration (PNG format only)
  updateFavicon(domainConfig) {
    // Skip favicon manipulation during pre-rendering
    // The build-time SEO injection already set the correct favicon
    if (this.isPreRendering()) {
      return;
    }

    // Prevent duplicate updates for the same domain
    if (this.currentDomainId === domainConfig?.id) {
      return;
    }

    if (!domainConfig || !domainConfig.favicon) {
      this.setDefaultFavicon();
      return;
    }

    // Set current domain to prevent duplicates
    this.currentDomainId = domainConfig.id;

    const faviconPath = this.findFaviconWithFormat(domainConfig.favicon);

    // Update favicon
    this.updateFaviconLink(faviconPath);
    
    // Update apple touch icon (uses same icon as favicon)
    this.updateAppleTouchIcon(faviconPath);
    
    // Update manifest
    this.updateManifest(domainConfig);
    
    // Update page title
    this.updatePageTitle(domainConfig);
    
    // Update meta description
    this.updateMetaDescription(domainConfig);
    
    // Update theme color
    this.updateThemeColor(domainConfig);
  }

  // Find favicon with PNG format only
  findFaviconWithFormat(faviconPath) {
    if (!faviconPath) return null;

    // If path already has .png extension, return as is
    if (faviconPath.match(/\.png$/i)) {
      return faviconPath;
    }

    // Remove any existing extension and add .png
    const basePath = faviconPath.replace(/\.(ico|png|jpg|jpeg)$/i, '');
    return `${basePath}.png`;
  }

  // Update favicon link in document head (PNG format only)
  updateFaviconLink(faviconPath) {
    // Check if this favicon is already set to prevent duplicates
    const existingFavicon = document.querySelector('link[rel="icon"]');
    if (existingFavicon && existingFavicon.href.includes(faviconPath.replace(/\.png$/i, ''))) {
      return; // Already set, no need to update
    }

    // Remove existing favicon links
    const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
    existingFavicons.forEach(link => link.remove());

    // Add new favicon with cache-busting
    const faviconLink = document.createElement('link');
    faviconLink.rel = 'icon';
    faviconLink.href = `${faviconPath}?v=${Date.now()}`;
    faviconLink.type = 'image/png';
    
    document.head.appendChild(faviconLink);

    this.currentFavicon = faviconPath;
  }

  // Update apple touch icon
  updateAppleTouchIcon(appleTouchIconPath) {
    // Remove existing apple touch icon
    const existingAppleIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (existingAppleIcon) {
      existingAppleIcon.remove();
    }

    // Add new apple touch icon with cache-busting
    const appleTouchIconLink = document.createElement('link');
    appleTouchIconLink.rel = 'apple-touch-icon';
    appleTouchIconLink.href = `${appleTouchIconPath}?v=${Date.now()}`;
    appleTouchIconLink.sizes = '180x180';
    document.head.appendChild(appleTouchIconLink);

    this.currentAppleTouchIcon = appleTouchIconPath;
  }

  // Update manifest.json dynamically
  updateManifest(domainConfig) {
    // Skip during pre-rendering - blob URLs won't work in static HTML
    if (this.isPreRendering()) {
      return;
    }

    // Get domain-specific icon paths
    const domainIcon = domainConfig.icon || `/assets/icons/${domainConfig.id}`;
    const faviconPath = domainConfig.favicon || '/assets/favicons/default-favicon.png';
    
    
    const manifest = {
      short_name: domainConfig.name || 'Smarter Tracking',
      name: `${domainConfig.name} - ${domainConfig.tagline}`,
      icons: [
        {
          src: faviconPath,
          sizes: '64x64 32x32 24x24 16x16',
          type: 'image/png'
        },
        {
          src: domainIcon,
          type: 'image/png',
          sizes: '192x192'
        },
        {
          src: domainIcon,
          type: 'image/png',
          sizes: '512x512'
        }
      ],
      start_url: '.',
      display: 'standalone',
      theme_color: domainConfig.theme?.primary || '#452F80',
      background_color: domainConfig.theme?.background || '#ffffff'
    };

    // Create blob URL for manifest
    const manifestBlob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
    const manifestURL = URL.createObjectURL(manifestBlob);

    // Update manifest link
    const existingManifest = document.querySelector('link[rel="manifest"]');
    if (existingManifest) {
      existingManifest.href = manifestURL;
    } else {
      const manifestLink = document.createElement('link');
      manifestLink.rel = 'manifest';
      manifestLink.href = manifestURL;
      document.head.appendChild(manifestLink);
    }
  }

  // Update page title
  updatePageTitle(domainConfig) {
    // Page title is now handled by SEOHead component
    // const title = `${domainConfig.name} | ${domainConfig.tagline}`;
    // document.title = title;
  }

  // Update meta description - disabled to prevent conflicts with react-helmet-async
  updateMetaDescription(domainConfig) {
    // Meta description is now handled by SEOHead component
    // const existingDescription = document.querySelector('meta[name="description"]');
    // if (existingDescription) {
    //   existingDescription.content = domainConfig.description || 'Advanced asset tracking and fleet management solutions';
    // } else {
    //   const descriptionMeta = document.createElement('meta');
    //   descriptionMeta.name = 'description';
    //   descriptionMeta.content = domainConfig.description || 'Advanced asset tracking and fleet management solutions';
    //   document.head.appendChild(descriptionMeta);
    // }
  }

  // Update theme color
  updateThemeColor(domainConfig) {
    // Skip during pre-rendering - already set in build
    if (this.isPreRendering()) {
      return;
    }

    const existingThemeColor = document.querySelector('meta[name="theme-color"]');
    if (existingThemeColor) {
      existingThemeColor.content = domainConfig.theme?.primary || '#452F80';
    } else {
      const themeColorMeta = document.createElement('meta');
      themeColorMeta.name = 'theme-color';
      themeColorMeta.content = domainConfig.theme?.primary || '#452F80';
      document.head.appendChild(themeColorMeta);
    }
  }

  // Set default favicon
  setDefaultFavicon() {
    this.updateFaviconLink('/assets/favicons/default-favicon.png');
    this.updateAppleTouchIcon('/assets/favicons/default-favicon.png');
  }

  // Get current favicon
  getCurrentFavicon() {
    return this.currentFavicon;
  }

  // Get current apple touch icon
  getCurrentAppleTouchIcon() {
    return this.currentAppleTouchIcon;
  }
}

// Create singleton instance
export const faviconManager = new FaviconManager();
